import { Injectable } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { ArrayUtils, isOfNonZeroLength } from '@ftr/foundation'
import { SharedRoutingParams } from '@ftr/routing-paths'
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map } from 'rxjs'
import { QUERY_PARAM_SEPARATOR, mapFilterValueToQueryStringValue, mapQueryStringValueToFilterValue } from '../list'
import { ComplexFilter, ComplexFilterData, ComplexFilterSection, ComplexFilterValue } from './ComplexFilter'
import { RadioListFilter } from './RadioListFilter'
import { getComplexDateFilterFormattedDate } from './date/ComplexDateFilterUtils'

/**
 * This service handles complex filter updates and corresponding query string parameters.
 */
@Injectable()
export class ComplexFilterService {
  readonly clearAllSubject = new BehaviorSubject<undefined>(undefined)

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  /**
   * Emits updates for the given filter value
   */
  listenToFilterChange(filters: ComplexFilter[]): Observable<ComplexFilterData> {
    return combineLatest([
      this.route.queryParams.pipe(
        distinctUntilChanged((prev, curr) => compareParams(prev, curr, SharedRoutingParams.Page)),
      ),
      /* Covers the case when there are no query param updates,
         but we want the dependent components to pick up a change */
      this.clearAllSubject,
    ]).pipe(
      map(([queryParams]) =>
        arrayToComplexFilterData(
          filters.map(filter => ({
            [filter.filterKey]: this.getFilterValue(filter, queryParams[filter.filterKey]),
          })),
        ),
      ),
    )
  }

  /**
   * Updates the query string with the new filter value
   */
  async updateFilter(
    data: ComplexFilterData,
    options: { clearAll?: boolean; resetPage?: boolean } = {},
  ): Promise<void> {
    const { clearAll = false, resetPage = true } = options
    const queryParams = {
      ...this.route.snapshot.queryParams,
      ...arrayToComplexFilterData(
        Object.entries(data).map(([key, value]) => ({
          [key]: this.mapFilterValueToQueryStringValue(value),
        })),
      ),
    }

    // In some cases we do not want to reset the page, e.g. on initial load
    if (resetPage) {
      queryParams.page = undefined
    }

    await this.router
      .navigate([], {
        replaceUrl: true,
        relativeTo: this.route,
        queryParamsHandling: 'merge',
        queryParams,
        state: {
          clearAll,
        },
      })
      .then(result => {
        /* Router returns `true` on successful navigation.
           Components dependent on this service update themselves based on query param changes.
           When we tick some filters, don't apply them and clear all, navigation doesn't happen,
           but we still want the components to get an update.
           The check below addresses this case specifically. */
        if (!result && clearAll) {
          this.clearAllSubject.next(undefined)
        }
      })
  }

  /**
   * Resets provided filters to default values or undefined, excluding the ones with noClear
   */
  async clearAll(filters: ComplexFilter[]): Promise<void> {
    const defaults = arrayToComplexFilterData(
      filters
        .filter(filter => !filter.noClear)
        .map(filter => {
          const values = [filter]
            .concat(filter instanceof RadioListFilter && filter.additionalFilters ? filter.additionalFilters : [])
            .map(f => f.defaultValue || undefined)
          return { [filter.filterKey]: values && values.length === 1 ? values[0] : values } as ComplexFilterData
        }),
    )
    return this.updateFilter(defaults, { clearAll: true })
  }

  /**
   * Sections and subsections are only required for component rendering,
   * we need a flat array of filters to work with
   */
  getFilters(sections: ComplexFilterSection[]): ComplexFilter[] {
    return sections
      .map(s => s.subsections)
      .reduce((acc, val) => acc.concat(val))
      .map(s => s.filters)
      .reduce((acc, val) => acc.concat(val))
  }

  /**
   * Depending on the size of the screen filters can be applied immediately on change
   * or when the user applies them manually.
   * clearAll should always be applied immediately, so we pass a truthy value to the router's state
   * just for this one navigation event.
   * This helps bypass applyImmediately and forces complex filter to update itself.
   */
  get isClearAll(): boolean | undefined {
    return this.router.getCurrentNavigation()?.extras?.state?.clearAll
  }

  private mapFilterValueToQueryStringValue(value: ComplexFilterValue): ComplexFilterValue {
    if (!value) {
      return undefined
    }
    if (Array.isArray(value)) {
      const queryParamArray = value.map(v => mapFilterValueToQueryStringValue(v || '')).join(QUERY_PARAM_SEPARATOR)
      return queryParamArray.length ? queryParamArray : undefined
    }

    return mapFilterValueToQueryStringValue(value)
  }

  /**
   * Returns a value for the given filter
   */
  private getFilterValue(filter: ComplexFilter, value: string | undefined): ComplexFilterValue {
    if (filter.type === 'date') {
      const queryDateValue = mapQueryStringValueToFilterValue(value)
      return queryDateValue ? getComplexDateFilterFormattedDate(queryDateValue) : undefined
    }

    const queryStringValueSplit = (value?.split(QUERY_PARAM_SEPARATOR) || []).map(q =>
      mapQueryStringValueToFilterValue(q),
    )

    if (filter.type === 'radio-list') {
      const values = [filter]
        .concat(filter instanceof RadioListFilter && filter.additionalFilters ? filter.additionalFilters : [])
        .map((q, i) => (queryStringValueSplit && queryStringValueSplit[i] ? queryStringValueSplit[i] : q.defaultValue))

      return values && values.length === 1 ? values[0] : values
    }

    if (!queryStringValueSplit) {
      return undefined
    }

    if (!Array.isArray(queryStringValueSplit)) {
      return queryStringValueSplit || filter.defaultValue
    }

    return queryStringValueSplit.filter(isOfNonZeroLength)
  }
}

function arrayToComplexFilterData(arr: Record<string, ComplexFilterValue>[]): ComplexFilterData {
  return arr.reduce((acc, val) => ({ ...acc, ...val }), {})
}

/**
 * To prevent emission when page is the only one param that has changed and the rest of param values are equal.
 * This case will be handled by the list component.
 */
export function compareParams(one: Params, two: Params, pageParam: string): boolean {
  return (
    (one[pageParam] !== undefined || two[pageParam] !== undefined) &&
    Object.entries(one)
      .filter(([key]) => key !== pageParam)
      .every(([key, value]) =>
        Array.isArray(value) && Array.isArray(two[key])
          ? ArrayUtils.shallowEquals(value, two[key])
          : value === two[key],
      )
  )
}

/**
 * Calculates the number of filter options currently applied
 * @param filters complex filter data to work with
 * @param excludedFilterKeys optional filter keys to exclude from the total amount
 * @param filterKeysCountAsOne optional filter keys to count an array value as one selected filter (e.g. date range)
 * @param filterKey an optional filter key to only include this one filter
 */
export function filtersApplied(
  filters: ComplexFilterData,
  excludedFilterKeys: string[] = [],
  filterKeysCountAsOne: string[] = [],
  filterKey?: string,
): number {
  if (!filters || Object.keys(filters).length === 0) {
    return 0
  }

  return (
    Object.entries(filters)
      .filter(([key]) => !excludedFilterKeys.includes(key))
      // Just this one filter if its filterKey is provided
      .filter(([key]) => (filterKey ? key === filterKey : true))
      .map(([key, value]) => [
        key,
        (filterKeysCountAsOne || []).includes(key) && Array.isArray(value) ? !!value?.length : value,
      ])
      .reduce((acc, [_, value]) => (acc += Array.isArray(value) ? value.length : value ? 1 : 0), 0)
  )
}

export function isEqualComplexFilterValue<T extends string = string>(
  one: ComplexFilterValue<T> | null,
  two: ComplexFilterValue<T> | null,
): boolean {
  if (Array.isArray(one) && Array.isArray(two)) {
    return ArrayUtils.shallowEquals(one, two)
  }

  return one === two
}

/**
 * If a multi-select filter has a limit on the number of selected options,
 * disable unselected form controls when the limit is reached.
 * Can't disable selected controls, we want the user to be able to unselect them.
 * Also re-enables form controls when the selection is under the limit.
 */
export function checkMaxSelectedOptions(
  formGroup: UntypedFormGroup,
  filterValue: string[],
  maxSelectedOptions: number | undefined,
): void {
  if (!maxSelectedOptions) {
    return
  }

  Object.keys(formGroup.controls).forEach(key => {
    const control = formGroup.controls[key]
    control.enable({ emitEvent: false })
    if (filterValue.length >= maxSelectedOptions && !control.value) {
      control.disable({ emitEvent: false })
    }
  })
}
